export interface Coordinate {
  lat: number
  lng: number
}

export interface ParseError {
  line: number
  error: string
  original: string
}

export interface ParseResult {
  valid: Coordinate[]
  invalid: ParseError[]
  warnings: string[]
}

/**
 * Detect if the first line is a header
 */
function isHeaderLine(line: string): boolean {
  const headerPattern = /(latitude|longitude|lat|lng)/i
  return headerPattern.test(line)
}

/**
 * Validate coordinate values
 */
function isValidCoordinate(lat: number, lng: number): boolean {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
}

/**
 * Remove duplicate consecutive coordinates (rounded to 6 decimals)
 */
function removeDuplicates(coordinates: Coordinate[]): { coords: Coordinate[]; removed: number } {
  if (coordinates.length === 0) return { coords: [], removed: 0 }
  
  const result: Coordinate[] = [coordinates[0]]
  let removed = 0
  
  for (let i = 1; i < coordinates.length; i++) {
    const prev = result[result.length - 1]
    const curr = coordinates[i]
    
    const prevLatRounded = Math.round(prev.lat * 1000000) / 1000000
    const prevLngRounded = Math.round(prev.lng * 1000000) / 1000000
    const currLatRounded = Math.round(curr.lat * 1000000) / 1000000
    const currLngRounded = Math.round(curr.lng * 1000000) / 1000000
    
    if (prevLatRounded !== currLatRounded || prevLngRounded !== currLngRounded) {
      result.push(curr)
    } else {
      removed++
    }
  }
  
  return { coords: result, removed }
}

/**
 * Calculate signed area to determine winding order
 * Negative = clockwise, Positive = counter-clockwise
 */
function calculateSignedArea(coordinates: Coordinate[]): number {
  let area = 0
  for (let i = 0; i < coordinates.length; i++) {
    const j = (i + 1) % coordinates.length
    area += coordinates[i].lng * coordinates[j].lat
    area -= coordinates[j].lng * coordinates[i].lat
  }
  return area / 2
}

/**
 * Check and correct winding order (PostGIS expects counter-clockwise)
 */
function ensureCounterClockwise(coordinates: Coordinate[]): { coords: Coordinate[]; reversed: boolean } {
  if (coordinates.length < 3) return { coords: coordinates, reversed: false }
  
  const signedArea = calculateSignedArea(coordinates)
  
  if (signedArea < 0) {
    return { coords: [...coordinates].reverse(), reversed: true }
  }
  
  return { coords: coordinates, reversed: false }
}

/**
 * Try to parse a line in various formats
 */
function parseLine(line: string, lineNumber: number): { coord: Coordinate | null; error: string | null } {
  const trimmed = line.trim()
  if (!trimmed) return { coord: null, error: null } // Skip empty lines
  
  // Try JSON format: {lat: X, lng: Y} or {"lat": X, "lng": Y}
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    try {
      const obj = JSON.parse(trimmed)
      if (typeof obj.lat === "number" && typeof obj.lng === "number") {
        if (isValidCoordinate(obj.lat, obj.lng)) {
          return { coord: { lat: obj.lat, lng: obj.lng }, error: null }
        }
        return { coord: null, error: "Coordinates out of valid range" }
      }
    } catch {
      return { coord: null, error: "Invalid JSON format" }
    }
  }
  
  // Try comma-separated: lat,lng
  if (trimmed.includes(",")) {
    const parts = trimmed.split(",").map(p => p.trim())
    if (parts.length === 2) {
      const lat = parseFloat(parts[0])
      const lng = parseFloat(parts[1])
      if (!isNaN(lat) && !isNaN(lng)) {
        if (isValidCoordinate(lat, lng)) {
          return { coord: { lat, lng }, error: null }
        }
        return { coord: null, error: "Coordinates out of valid range" }
      }
    }
  }
  
  // Try tab or space-separated: lat lng or lat\tlng
  const parts = trimmed.split(/[\s\t]+/)
  if (parts.length === 2) {
    const lat = parseFloat(parts[0])
    const lng = parseFloat(parts[1])
    if (!isNaN(lat) && !isNaN(lng)) {
      if (isValidCoordinate(lat, lng)) {
        return { coord: { lat, lng }, error: null }
      }
      return { coord: null, error: "Coordinates out of valid range" }
    }
  }
  
  return { coord: null, error: "Unrecognized format" }
}

/**
 * Parse GeoJSON format (Polygon or MultiPoint)
 */
function parseGeoJSON(input: string): Coordinate[] | null {
  try {
    const geojson = JSON.parse(input)
    
    // Handle GeoJSON Polygon
    if (geojson.type === "Polygon" && Array.isArray(geojson.coordinates)) {
      // GeoJSON uses [lng, lat] order
      const ring = geojson.coordinates[0] // First ring (outer boundary)
      return ring.map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }))
    }
    
    // Handle GeoJSON MultiPoint
    if (geojson.type === "MultiPoint" && Array.isArray(geojson.coordinates)) {
      return geojson.coordinates.map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }))
    }
    
    // Handle GeoJSON Feature with Polygon geometry
    if (geojson.type === "Feature" && geojson.geometry?.type === "Polygon") {
      const ring = geojson.geometry.coordinates[0]
      return ring.map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }))
    }
  } catch {
    return null
  }
  return null
}

/**
 * Parse WKT (Well-Known Text) format
 */
function parseWKT(input: string): Coordinate[] | null {
  const trimmed = input.trim()
  
  // Match POLYGON((lng lat, lng lat, ...))
  const polygonMatch = trimmed.match(/POLYGON\s*\(\s*\(([^)]+)\)\s*\)/i)
  if (polygonMatch) {
    const coordsStr = polygonMatch[1]
    const coords = coordsStr.split(",").map(pair => {
      const [lng, lat] = pair.trim().split(/\s+/).map(parseFloat)
      return { lat, lng }
    }).filter(c => !isNaN(c.lat) && !isNaN(c.lng))
    
    // Remove last point if it's the same as first (WKT polygons are closed)
    if (coords.length > 1 && coords[0].lat === coords[coords.length - 1].lat && coords[0].lng === coords[coords.length - 1].lng) {
      coords.pop()
    }
    
    return coords.length >= 3 ? coords : null
  }
  
  // Match MULTIPOINT((lng lat), (lng lat), ...)
  const multipointMatch = trimmed.match(/MULTIPOINT\s*\(([^)]+)\)/i)
  if (multipointMatch) {
    const coordsStr = multipointMatch[1]
    const coords = coordsStr.split(/\),\s*\(|\(|\)/).filter(s => s.trim()).map(pair => {
      const [lng, lat] = pair.trim().split(/\s+/).map(parseFloat)
      return { lat, lng }
    }).filter(c => !isNaN(c.lat) && !isNaN(c.lng))
    
    return coords.length >= 3 ? coords : null
  }
  
  return null
}

/**
 * Parse WKB (Well-Known Binary) - hex encoded
 */
function parseWKB(input: string): Coordinate[] | null {
  // WKB parsing is complex, we'll support basic hex-encoded polygons
  // This is a simplified version - production would use a proper WKB parser library
  try {
    const trimmed = input.trim()
    
    // Check if it looks like hex-encoded WKB (starts with 01 or 00)
    if (!/^(01|00)[0-9A-Fa-f]+$/.test(trimmed)) {
      return null
    }
    
    // For now, we'll just detect it and suggest using WKT or GeoJSON instead
    // Full WKB parsing would require binary buffer manipulation
    return null
  } catch {
    return null
  }
}

/**
 * Main parser function
 */
export function parseCoordinates(input: string): ParseResult {
  const lines = input.split("\n")
  const valid: Coordinate[] = []
  const invalid: ParseError[] = []
  const warnings: string[] = []
  
  let startLine = 0
  
  // Check if first line is a header
  if (lines.length > 0 && isHeaderLine(lines[0])) {
    startLine = 1
  }
  
  const trimmedInput = input.trim()
  
  // Try GeoJSON format
  if (trimmedInput.startsWith("{") && (trimmedInput.includes('"type"') || trimmedInput.includes("'type'"))) {
    const geojsonCoords = parseGeoJSON(trimmedInput)
    if (geojsonCoords && geojsonCoords.length >= 3) {
      geojsonCoords.forEach(coord => {
        if (isValidCoordinate(coord.lat, coord.lng)) {
          valid.push(coord)
        } else {
          invalid.push({
            line: 1,
            error: "Coordinates out of valid range",
            original: `${coord.lat}, ${coord.lng}`
          })
        }
      })
      
      warnings.push("Parsed GeoJSON format")
      
      const { coords: deduped, removed: duplicatesRemoved } = removeDuplicates(valid)
      if (duplicatesRemoved > 0) {
        warnings.push(`Removed ${duplicatesRemoved} duplicate consecutive point(s)`)
      }
      
      const { coords: corrected, reversed } = ensureCounterClockwise(deduped)
      if (reversed) {
        warnings.push("Corrected polygon winding order to counter-clockwise")
      }
      
      return { valid: corrected, invalid, warnings }
    }
  }
  
  // Try WKT format
  if (/^(POLYGON|MULTIPOINT)\s*\(/i.test(trimmedInput)) {
    const wktCoords = parseWKT(trimmedInput)
    if (wktCoords && wktCoords.length >= 3) {
      wktCoords.forEach(coord => {
        if (isValidCoordinate(coord.lat, coord.lng)) {
          valid.push(coord)
        } else {
          invalid.push({
            line: 1,
            error: "Coordinates out of valid range",
            original: `${coord.lat}, ${coord.lng}`
          })
        }
      })
      
      warnings.push("Parsed WKT format")
      
      const { coords: deduped, removed: duplicatesRemoved } = removeDuplicates(valid)
      if (duplicatesRemoved > 0) {
        warnings.push(`Removed ${duplicatesRemoved} duplicate consecutive point(s)`)
      }
      
      const { coords: corrected, reversed } = ensureCounterClockwise(deduped)
      if (reversed) {
        warnings.push("Corrected polygon winding order to counter-clockwise")
      }
      
      return { valid: corrected, invalid, warnings }
    }
  }
  
  // Try WKB format (hex encoded)
  if (/^(01|00)[0-9A-Fa-f]{10,}$/.test(trimmedInput)) {
    const wkbCoords = parseWKB(trimmedInput)
    if (wkbCoords === null) {
      warnings.push("WKB format detected but not fully supported. Please convert to WKT or GeoJSON format.")
    }
  }
  
  // Try to detect JSON array format
  if (trimmedInput.startsWith("[") && trimmedInput.endsWith("]")) {
    try {
      const arr = JSON.parse(trimmedInput)
      if (Array.isArray(arr)) {
        arr.forEach((item, index) => {
          if (typeof item === "object" && item !== null && typeof item.lat === "number" && typeof item.lng === "number") {
            if (isValidCoordinate(item.lat, item.lng)) {
              valid.push({ lat: item.lat, lng: item.lng })
            } else {
              invalid.push({
                line: index + 1,
                error: "Coordinates out of valid range",
                original: JSON.stringify(item)
              })
            }
          } else {
            invalid.push({
              line: index + 1,
              error: "Invalid coordinate object",
              original: JSON.stringify(item)
            })
          }
        })
        
        // Process warnings
        const { coords: deduped, removed: duplicatesRemoved } = removeDuplicates(valid)
        if (duplicatesRemoved > 0) {
          warnings.push(`Removed ${duplicatesRemoved} duplicate consecutive point(s)`)
        }
        
        const { coords: corrected, reversed } = ensureCounterClockwise(deduped)
        if (reversed) {
          warnings.push("Corrected polygon winding order to counter-clockwise")
        }
        
        return { valid: corrected, invalid, warnings }
      }
    } catch {
      // Fall through to line-by-line parsing
    }
  }
  
  // Line-by-line parsing
  for (let i = startLine; i < lines.length; i++) {
    const { coord, error } = parseLine(lines[i], i + 1)
    
    if (coord) {
      valid.push(coord)
    } else if (error) {
      invalid.push({
        line: i + 1,
        error,
        original: lines[i].trim()
      })
    }
  }
  
  // Process warnings
  const { coords: deduped, removed: duplicatesRemoved } = removeDuplicates(valid)
  if (duplicatesRemoved > 0) {
    warnings.push(`Removed ${duplicatesRemoved} duplicate consecutive point(s)`)
  }
  
  const { coords: corrected, reversed } = ensureCounterClockwise(deduped)
  if (reversed) {
    warnings.push("Corrected polygon winding order to counter-clockwise")
  }
  
  return { valid: corrected, invalid, warnings }
}
